import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Save, Truck, ListChecks, History } from 'lucide-react';
import { toast } from 'sonner';

const DOC_LABELS = {
  sales_order: 'Sales Order',
  production_order: 'Production Order',
  packing_slip: 'Packing Slip',
  bill_of_lading: 'Bill of Lading',
  repair_order: 'Repair Order',
};

const RECENT_HOURS = 72;

const PolicyAuditPanel = ({ api }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const res = await api().get('/admin/policy-audit-log?limit=100');
      setRows(res.data || []);
    } catch { toast.error('Audit log load failed'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  return (
    <Card data-testid="policy-audit-panel">
      <CardHeader className="flex-row items-center justify-between gap-2 pb-3">
        <div className="flex items-center gap-2"><History className="w-4 h-4 text-slate-600" /><CardTitle className="text-base">Policy Audit Log</CardTitle></div>
        <Button variant="outline" size="sm" onClick={load} data-testid="policy-audit-refresh"><ListChecks className="w-3.5 h-3.5 mr-1" />Refresh</Button>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-500 mb-3">Every time a user hits "Issue" on a doc that fails the required-fields policy, the attempt is logged here. Use it to see which required fields cause the most friction — if the same field blocks everyone, consider dropping it from the policy.</p>
        {loading ? <p className="text-xs text-slate-500">Loading…</p> : (
          rows.length === 0 ? <p className="text-xs text-slate-500 italic">No blocked attempts yet. 🎉</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse" data-testid="policy-audit-table">
                <thead>
                  <tr className="border-b bg-slate-50 text-left">
                    <th className="p-2">When</th>
                    <th className="p-2">User</th>
                    <th className="p-2">Doc Type</th>
                    <th className="p-2">Doc #</th>
                    <th className="p-2">Customer</th>
                    <th className="p-2">Missing Fields</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b last:border-b-0 hover:bg-slate-50" data-testid={`policy-audit-row-${i}`}>
                      <td className="p-2 whitespace-nowrap mono text-slate-600">{(r.timestamp || '').replace('T', ' ').slice(0, 19)}</td>
                      <td className="p-2">{r.user_name || r.user_email || <span className="text-slate-400 italic">{r.user_id?.slice(0, 8) || 'anon'}</span>}</td>
                      <td className="p-2">{DOC_LABELS[(r.details || {}).doc_type] || (r.details || {}).doc_type}</td>
                      <td className="p-2 mono">{(r.details || {}).doc_number || '—'}</td>
                      <td className="p-2">{(r.details || {}).customer_name || '—'}</td>
                      <td className="p-2"><span className="inline-flex flex-wrap gap-1">{((r.details || {}).missing || []).map(k => <span key={k} className="bg-amber-100 text-amber-900 border border-amber-300 rounded-sm px-1.5 py-0.5 mono text-[11px]">{k}</span>)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10px] text-slate-400 mt-2">Showing up to 100 most-recent blocks.</p>
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
};

export const ShippingPolicyPanel = ({ api, canEdit }) => {
  const [defaults, setDefaults] = useState({ ship_from_name: '', ship_from_address: '', ship_from_contact: '' });
  const [policy, setPolicy] = useState({});
  const [allowed, setAllowed] = useState({});
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([
        api().get('/admin/shipping-defaults'),
        api().get('/admin/required-fields-policy'),
      ]);
      setDefaults({
        ship_from_name: d.data?.ship_from_name || '',
        ship_from_address: d.data?.ship_from_address || '',
        ship_from_contact: d.data?.ship_from_contact || '',
      });
      setPolicy(p.data?.policy || {});
      setAllowed(p.data?.allowed_keys || {});
    } catch (e) { toast.error('Load failed'); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const saveDefaults = async () => {
    setSavingDefaults(true);
    try {
      await api().put('/admin/shipping-defaults', defaults);
      toast.success('Default Ship-From saved — new BoLs will auto-populate it');
    } catch { toast.error('Save failed'); } finally { setSavingDefaults(false); }
  };

  const togglePolicyKey = (docType, key) => {
    setPolicy(prev => {
      const curr = prev[docType] || [];
      const next = curr.includes(key) ? curr.filter(k => k !== key) : [...curr, key];
      return { ...prev, [docType]: next };
    });
  };

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      await api().put('/admin/required-fields-policy', policy);
      toast.success('Required-fields policy saved — enforced on next doc issue');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSavingPolicy(false); }
  };

  return (
    <div className="space-y-6" data-testid="shipping-policy-panel">
      <Card>
        <CardHeader className="flex-row items-center gap-2 pb-3">
          <Truck className="w-4 h-4 text-slate-600" />
          <CardTitle className="text-base">Default Ship-From</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Ship-From Name</Label><Input value={defaults.ship_from_name} onChange={e => setDefaults(d => ({ ...d, ship_from_name: e.target.value }))} disabled={!canEdit} data-testid="shipdef-name" placeholder="Your company / facility name" /></div>
          <div><Label>Ship-From Contact</Label><Input value={defaults.ship_from_contact} onChange={e => setDefaults(d => ({ ...d, ship_from_contact: e.target.value }))} disabled={!canEdit} data-testid="shipdef-contact" placeholder="Dock supervisor / shipper name" /></div>
          <div className="md:col-span-2"><Label>Ship-From Address</Label><Textarea rows={3} value={defaults.ship_from_address} onChange={e => setDefaults(d => ({ ...d, ship_from_address: e.target.value }))} disabled={!canEdit} data-testid="shipdef-address" placeholder={"Street\nCity, State ZIP"} /></div>
          <div className="md:col-span-2 flex items-center justify-between">
            <p className="text-xs text-slate-500">Auto-fills BoL ship_from_* when operators leave those blank.</p>
            {canEdit && <Button onClick={saveDefaults} disabled={savingDefaults} size="sm" data-testid="shipdef-save-btn"><Save className="w-3.5 h-3.5 mr-1" />{savingDefaults ? 'Saving…' : 'Save Defaults'}</Button>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center gap-2 pb-3">
          <ListChecks className="w-4 h-4 text-slate-600" />
          <CardTitle className="text-base">Required-Fields Policy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-xs text-slate-500">Checked fields must be set on the doc before it can transition out of Draft (Sales/Production/Packing/BoL → <strong>Issued</strong>, Repair → <strong>Received</strong>). Leaving a doc_type unchecked = no enforcement.</p>
          {Object.keys(DOC_LABELS).map(dt => {
            const keys = allowed[dt] || [];
            const checked = policy[dt] || [];
            return (
              <div key={dt} className="border rounded-sm p-3" data-testid={`policy-${dt}`}>
                <div className="font-semibold text-sm mb-2">{DOC_LABELS[dt]}</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {keys.map(k => (
                    <label key={k} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`policy-${dt}-${k}`}>
                      <Checkbox checked={checked.includes(k)} onCheckedChange={() => canEdit && togglePolicyKey(dt, k)} disabled={!canEdit} />
                      <span className="mono">{k}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
          {canEdit && (
            <div className="flex justify-end"><Button onClick={savePolicy} disabled={savingPolicy} size="sm" data-testid="policy-save-btn"><Save className="w-3.5 h-3.5 mr-1" />{savingPolicy ? 'Saving…' : 'Save Policy'}</Button></div>
          )}
        </CardContent>
      </Card>

      <PolicyAuditPanel api={api} />
    </div>
  );
};

export default ShippingPolicyPanel;
